import { PileId, Player, RoundResult } from 'game-logic';
import { GameServices } from '../../services/game-services';
import { RoundedLabelViewModel } from '../components/rounded-label-view-model';
import { PlayerCardStackViewModel } from '../player/player-card-stack-view-model';
import { PlayerMiniBetViewModel } from '../player/player-mini-bet-view-model';
import { ChipStackViewModel } from '../chips/stack/chip-stack-view-model';
import { Subject } from 'rxjs';
import { assertNever, delay } from 'src/games/common/helper';
import { UxMetrics } from '../../model/ux-metrics';
import { BlackjackStylesType, BlackjackTranslationsType } from '../../model/blackjack-types';

export class PlayerHandViewModel {
  public get layoutConfig() {
    return this.gameServices.gameMetricsService.gameLayoutConfig;
  }
  public get interfaceController() {
    return this.gameServices.interfaceController;
  }
  public get scoreText$() {
    return this.playerScoreViewModel.currentText$;
  }
  public get resultText$() {
    return this.playerResultViewModel.currentText$;
  }
  public get betStackAnimationTrigger$() {
    return this.betStackAnimationTrigger.asObservable();
  }
  public get doubleStackAnimationTrigger$() {
    return this.doubleStackAnimationTrigger.asObservable();
  }
  public get splitStackAnimationTrigger$() {
    return this.splitStackAnimationTrigger.asObservable();
  }
  public get loseChipsAnimationTrigger$() {
    return this.loseChipsAnimationTrigger.asObservable();
  }
  public get winChipsAnimationTrigger$() {
    return this.winChipsAnimationTrigger.asObservable();
  }

  public readonly playerMiniBetViewModel: PlayerMiniBetViewModel;
  public readonly playerCardStackViewModel: PlayerCardStackViewModel;
  public readonly playerScoreViewModel: RoundedLabelViewModel;
  public readonly playerResultViewModel: RoundedLabelViewModel;

  private betStackAnimationTrigger = new Subject<void>();
  private doubleStackAnimationTrigger = new Subject<void>();
  private splitStackAnimationTrigger = new Subject<void>();
  private loseChipsAnimationTrigger = new Subject<boolean>();
  private winChipsAnimationTrigger = new Subject<{ hasDoubled: boolean; result: RoundResult }>();

  private resultTimeoutId: NodeJS.Timeout | null = null;

  constructor(
    private gameServices: GameServices,
    readonly playerId: PileId,
    private jitterSeed: number,
  ) {
    this.playerMiniBetViewModel = new PlayerMiniBetViewModel(gameServices);
    this.playerCardStackViewModel = new PlayerCardStackViewModel(gameServices, playerId);
    this.playerScoreViewModel = new RoundedLabelViewModel(
      gameServices,
      '',
      gameServices.gameThemeService.getNumericalRGB(BlackjackStylesType.CardBackground),
      gameServices.gameThemeService.getNumericalAlpha(BlackjackStylesType.CardBackground),
    );
    this.playerResultViewModel = new RoundedLabelViewModel(gameServices, '', 0x000000, 0.9);
  }

  public resetGameScene() {
    // TODO: Do I need to reset this one?
    //this.playerMiniBetViewModel.resetGameScene();
    this.playerCardStackViewModel.resetGameScene();

    if (this.resultTimeoutId) {
      clearTimeout(this.resultTimeoutId);
      this.resultTimeoutId = null;
    }

    this.playerScoreViewModel.setText('');
    this.playerResultViewModel.setText('');
    this.setBetDealAmount(0);
    this.setBetDoubled(false);
  }

  public setJitterSeed(seed: number): void {
    this.jitterSeed = seed;
  }

  public playerBet(betAmount: number): void {
    if (this.playerId !== PileId.Player1) {
      return;
    }
    this.setBetDealAmount(betAmount);

    this.betStackAnimationTrigger.next();
  }

  public async playerBetDoubled(player: Player): Promise<void> {
    if (this.playerId !== player.playerId) {
      return;
    }
    // TODO: Refactor this when time allows
    this.doubleStackAnimationTrigger.next();

    await delay(UxMetrics.ChipStackAppear + UxMetrics.ChipStackTransition);
    this.setBetDealAmount(player.currentBet * 2);
    this.setBetDoubled(player.hasDoubled);
    await delay(UxMetrics.ChipStackPostDelay);
  }

  public async playerBetSplit(player: Player): Promise<void> {
    if (this.playerId !== player.playerId) {
      return;
    }
    // TODO: Refactor this when time allows
    this.splitStackAnimationTrigger.next();

    await delay(UxMetrics.ChipStackAppear + UxMetrics.ChipStackTransition);
    this.setBetDealAmount(player.currentBet);
    await delay(UxMetrics.ChipStackPostDelay);
  }

  public async transferCardToCurrent(cardIndex: number | undefined): Promise<void> {
    await this.playerCardStackViewModel.transferCardToCurrent(cardIndex);
  }

  public addCard(player: Player, cardIndex: number | undefined): void {
    this.playerCardStackViewModel.addCard(player, cardIndex);
  }

  public removeCard(cardIndex: number | undefined): void {
    this.playerCardStackViewModel.removeCard(cardIndex);
  }

  public updateScore(player: Player) {
    if (this.playerId !== player.playerId) {
      return;
    }
    if (player.allFaceCards.length > 0) {
      this.playerScoreViewModel.setText(player.displayScore);
    }
  }

  public clearScore(player: Player): void {
    if (this.playerId !== player.playerId) {
      return;
    }

    if (this.resultTimeoutId) {
      clearTimeout(this.resultTimeoutId);
      this.resultTimeoutId = null;
    }

    this.playerScoreViewModel.setText('');
    this.playerResultViewModel.setText('');
    this.setBetDealAmount(0);
    this.setBetDoubled(false);
  }

  public setResult(player: Player, result: RoundResult): void {
    if (this.playerId !== player.playerId) {
      return;
    }

    this.resultTimeoutId = setTimeout(
      () => {
        this.playerResultViewModel.setText(this.resultLabel(result));

        if (result === RoundResult.CroupierWin || result === RoundResult.PlayerBust) {
          this.loseChipsAnimationTrigger.next(player.hasDoubled);
        } else {
          this.winChipsAnimationTrigger.next({ hasDoubled: player.hasDoubled, result });
        }
      },
      UxMetrics.ResultDelay + UxMetrics.ResultAnimateIn + UxMetrics.ResultAnimateOut,
    );
  }

  public generateChipStackViewModel(): ChipStackViewModel {
    return new ChipStackViewModel(this.gameServices, this.jitterSeed);
  }

  private setBetDealAmount(betAmount: number): void {
    this.playerMiniBetViewModel.setBetAmount(betAmount);
  }

  private setBetDoubled(doubled: boolean): void {
    this.playerMiniBetViewModel.setBetDoubled(doubled);
  }

  public resultLabel(result: RoundResult): string {
    const translationService = this.gameServices.gameTranslationService;

    switch (result) {
      case RoundResult.Push:
        return translationService.getTranslation(BlackjackTranslationsType.Push);
      case RoundResult.CroupierWin:
        return translationService.getTranslation(BlackjackTranslationsType.Lost);
      case RoundResult.PlayerWin:
        return translationService.getTranslation(BlackjackTranslationsType.Won);
      case RoundResult.PlayerBust:
        return translationService.getTranslation(BlackjackTranslationsType.Bust);
      case RoundResult.PlayerBlackjack:
        return translationService.getTranslation(BlackjackTranslationsType.Blackjack);
      default:
        assertNever(result, 'Invalid result state');
    }
  }
}
